// The street, drawn. One canvas, three modes: the round (`draw`), the title's
// ambient loop (`drawAmbient`), My Street (`drawMyStreet`) and the national
// map (`drawAustralia`).
//
// WORLD UNITS. The level is 390 wide and 1560 tall; the camera maps 390 world
// units to the canvas width and follows the lead spark vertically. Everything
// below draws in world units on a transform the camera sets, so a phone and a
// letterboxed desktop column render the same picture.
//
// PERFORMANCE. Sixty frames a second on an older phone means no per-frame
// allocation in the hot loops: edge geometry, house placements and trees are
// precomputed per level; bulb glows are pre-rendered sprites keyed by colour
// and pixel radius; particles live in a fixed pool; spark trails in ring
// buffers. Colours come from lib/palette.js (CSS custom properties), never
// literals.

/* global performance */
import { palette, withAlpha, mix } from './palette.js';
import {
  drawHouse, drawGumTree, drawCockatoo, drawBauble, drawStar, drawTangle,
  drawDecoration, drawMomentIcon, glow, jitter, roundRect, starPath,
} from './sprites.js';

const TAU = Math.PI * 2;
const WORLD_W = 390;
const WORLD_H = 1560;
const HORIZON_Y = 95;
const JUNCTION_R = 28;           // ~56 world-unit diameter
const TRAIL_LEN = 18;
const MAX_PARTICLES = 120;
const MAX_SPARKS = 4;
const AUSTRALIA = [
  [0.715, 0.02], [0.74, 0.10], [0.775, 0.18], [0.80, 0.24], [0.86, 0.31], [0.90, 0.38],
  [0.925, 0.44], [0.915, 0.52], [0.885, 0.60], [0.86, 0.66], [0.82, 0.72], [0.77, 0.76],
  [0.72, 0.775], [0.68, 0.755], [0.64, 0.76], [0.60, 0.745], [0.565, 0.70], [0.55, 0.66],
  [0.535, 0.70], [0.505, 0.68], [0.48, 0.71], [0.44, 0.665], [0.38, 0.63], [0.31, 0.635],
  [0.24, 0.66], [0.17, 0.685], [0.115, 0.665], [0.085, 0.60], [0.07, 0.52], [0.075, 0.44],
  [0.09, 0.36], [0.07, 0.31], [0.11, 0.25], [0.17, 0.20], [0.22, 0.15], [0.27, 0.13],
  [0.30, 0.07], [0.35, 0.10], [0.40, 0.08], [0.43, 0.04], [0.48, 0.05], [0.52, 0.09],
  [0.56, 0.11], [0.575, 0.18], [0.60, 0.245], [0.64, 0.24], [0.675, 0.17], [0.695, 0.09],
];
const TASMANIA = [[0.715, 0.855], [0.775, 0.875], [0.765, 0.955], [0.705, 0.95], [0.68, 0.895]];
// Towns with the milestone tier that switches them on. Tier 0 is always on.
const TOWNS = [
  [0.885, 0.615, 0], [0.735, 0.765, 0], [0.915, 0.445, 0], [0.09, 0.575, 0], [0.55, 0.69, 0],
  [0.84, 0.675, 1], [0.74, 0.925, 1], [0.425, 0.085, 1], [0.90, 0.585, 1], [0.92, 0.47, 1],
  [0.79, 0.225, 2], [0.75, 0.14, 2], [0.70, 0.78, 2], [0.87, 0.64, 2], [0.72, 0.895, 2],
  [0.48, 0.43, 3], [0.22, 0.175, 3], [0.27, 0.555, 3], [0.68, 0.73, 3], [0.86, 0.43, 3], [0.80, 0.30, 3],
  [0.64, 0.68, 4], [0.78, 0.71, 4], [0.86, 0.56, 4], [0.62, 0.35, 4], [0.14, 0.24, 4], [0.30, 0.64, 4],
  [0.20, 0.66, 5], [0.10, 0.44, 5], [0.55, 0.15, 5], [0.72, 0.62, 5], [0.66, 0.48, 5], [0.36, 0.56, 5],
  [0.45, 0.60, 6], [0.83, 0.52, 6], [0.58, 0.58, 6], [0.30, 0.30, 6], [0.40, 0.20, 6], [0.76, 0.52, 6], [0.15, 0.62, 6],
];

export function createRenderer(canvas, options = {}) {
  const ctx = canvas.getContext('2d', { alpha: false });
  let P = palette();
  let opts = { highContrast: false, reducedMotion: false, ...options };

  // Canvas size in CSS px and the device ratio.
  let cw = 1; let ch = 1; let dpr = 1;
  // Camera in world units.
  let scale = 1;               // canvas px per world unit (CSS px)
  let followScale = 1;
  let camY = 0;
  let camX = 0;                // world x at the left edge (0 unless letterboxing the pull-back)
  let camInit = false;
  let skyGrad = null;
  let vignette = null;

  // Per-level precomputation.
  let lvl = null;
  let nodeById = new Map();
  let edgeGeom = [];           // { edge, ax, ay, bx, by, ux, uy, nx, ny, len, colorBase, horizontal }
  let geomById = new Map();
  let houses = [];             // { home, x, y, w, h, variant, community }
  let trees = [];
  let lawns = [];
  let fillers = [];            // background houses: scenery, never lit
  let lamps = [];              // street lamps down the avenues
  let avenues = [];            // world x of each street running down the map
  let crossStreets = [];       // world y of each street running across it
  const homeWarm = new Map();  // homeId → 0..1 easing to lit
  let cockatooHome = null;
  const collectedStars = new Set();
  let flashUntil = 0;

  // Sprite caches.
  const glowSprites = new Map();

  // Particles: fixed pool.
  const px = new Float32Array(MAX_PARTICLES);
  const py = new Float32Array(MAX_PARTICLES);
  const pvx = new Float32Array(MAX_PARTICLES);
  const pvy = new Float32Array(MAX_PARTICLES);
  const plife = new Float32Array(MAX_PARTICLES);
  const pmax = new Float32Array(MAX_PARTICLES);
  const pcol = new Array(MAX_PARTICLES).fill(null);
  const psize = new Float32Array(MAX_PARTICLES);
  let pcount = 0;

  // Trails: ring buffers per spark.
  const trailX = Array.from({ length: MAX_SPARKS }, () => new Float32Array(TRAIL_LEN));
  const trailY = Array.from({ length: MAX_SPARKS }, () => new Float32Array(TRAIL_LEN));
  const trailN = new Int32Array(MAX_SPARKS);
  const trailHead = new Int32Array(MAX_SPARKS);

  // ---------------------------------------------------------------------
  // Sizing and camera

  function resize() {
    const rect = canvas.getBoundingClientRect();
    cw = Math.max(1, Math.round(rect.width));
    ch = Math.max(1, Math.round(rect.height));
    dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(ch * dpr);
    followScale = cw / WORLD_W;
    if (!camInit) scale = followScale;
    skyGrad = null;
    vignette = null;
    glowSprites.clear();
  }

  function setOptions(next) {
    opts = { ...opts, ...next };
    P = palette();
    skyGrad = null;
    vignette = null;
    glowSprites.clear();
  }

  function viewH() { return ch / scale; }

  function setTransform() {
    ctx.setTransform(scale * dpr, 0, 0, scale * dpr, -camX * scale * dpr, -camY * scale * dpr);
  }

  function resetTransform() {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Screen (CSS px, relative to canvas) → world. */
  function toWorld(sx, sy) {
    return { x: sx / scale + camX, y: sy / scale + camY };
  }

  function toScreen(wx, wy) {
    return { x: (wx - camX) * scale, y: (wy - camY) * scale };
  }

  function camera() {
    return { scale, camX, camY, width: cw, height: ch };
  }

  function clampCam(y) {
    const vh = viewH();
    return Math.max(0, Math.min(WORLD_H - vh, y));
  }

  // ---------------------------------------------------------------------
  // Level precomputation

  function setLevel(level) {
    lvl = level;
    nodeById = new Map(level.nodes.map((n) => [n.id, n]));
    edgeGeom = level.edges.map((edge, i) => {
      const a = nodeById.get(edge.from);
      const b = nodeById.get(edge.to);
      const dx = b.x - a.x; const dy = b.y - a.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      return {
        edge, ax: a.x, ay: a.y, bx: b.x, by: b.y, ux: dx / len, uy: dy / len, nx: -dy / len, ny: dx / len, len,
        colorBase: i % 5,
        horizontal: Math.abs(dx) > Math.abs(dy),
      };
    });
    geomById = new Map(edgeGeom.map((g) => [g.edge.id, g]));

    houses = level.homes.map((home, i) => {
      const g = geomById.get(home.edgeIds[0]);
      const community = !!home.community;
      const w = community ? 64 : 58 + Math.floor(jitter(level.seed + i, 7) * 12);
      const h = community ? 46 : 40 + Math.floor(jitter(level.seed + i, 8) * 8);
      let x; let y;
      if (g.horizontal) {
        x = home.x;
        y = home.y + (community ? 44 : 40);
      } else {
        const side = home.x < 100 ? 1 : home.x > 290 ? -1 : (jitter(level.seed + i, 9) > 0.5 ? 1 : -1);
        x = home.x + side * (community ? 62 : 52);
        y = home.y + 24;
      }
      const variant = Math.floor(jitter(level.seed + i, 11) * 4);
      return { home, x, y, w, h, variant, community, index: i };
    });

    // --- the street grid ---------------------------------------------------
    // The cables follow the streets, so the streets are read back off the
    // nodes rather than hard-coded: one avenue down each column of nodes, one
    // cross street along each row. Without the cross streets three vertical
    // bands down a 1560-unit world read as columns, not as a neighbourhood.
    const colXs = [...new Set(level.nodes.map((n) => n.x))].sort((a, b) => a - b);
    avenues = colXs.filter((x, i) => i === 0 || x - colXs[i - 1] > 40);
    const rowYs = new Map();
    for (const n of level.nodes) {
      const r = n.row ?? Math.round(n.y / 100);
      rowYs.set(r, (rowYs.get(r) ?? 0) + n.y / 3);
    }
    crossStreets = [...rowYs.values()].sort((a, b) => a - b);

    // --- what stands beside the streets ------------------------------------
    lawns = houses.map((hs) => ({ x: hs.x, y: hs.y + 6, w: hs.w + 40, h: 22 }));

    const clear = (x, y, padX, padY) => {
      for (const hs of houses) if (Math.abs(hs.x - x) < hs.w / 2 + padX && Math.abs(hs.y - y) < hs.h + padY) return false;
      for (const n of level.nodes) if (Math.abs(n.x - x) < 34 && Math.abs(n.y - y) < 34) return false;
      for (const f of fillers) if (Math.abs(f.x - x) < f.w / 2 + padX && Math.abs(f.y - y) < f.h + padY) return false;
      return true;
    };

    // Background houses. Pure scenery — no cable, no bulbs, never lit — but
    // they are what turns "a diagram of eight homes" into "a street".
    fillers = [];
    avenues.forEach((lane, ai) => {
      for (const side of [-1, 1]) {
        for (let y = HORIZON_Y + 118; y < WORLD_H - 30; y += 62) {
          const s = level.seed * 0.0007 + lane * 0.31 + y * 0.17 + side * 3.1 + ai;
          if (jitter(s, 5) > 0.5) continue;               // gaps: not every block is built out
          const x = lane + side * (62 + jitter(s, 7) * 26);
          if (x < 14 || x > WORLD_W - 14) continue;
          const yy = y + (jitter(s, 8) - 0.5) * 22;
          const w = 34 + jitter(s, 9) * 18;
          const h = 22 + jitter(s, 10) * 11;
          if (!clear(x, yy, 14, 14)) continue;
          fillers.push({ x, y: yy, w, h, variant: Math.floor(jitter(s, 11) * 3), seed: s });
        }
      }
    });

    // Street lamps down each avenue. Staggered per avenue so they never line
    // up into a grid across the screen.
    lamps = [];
    avenues.forEach((ax, ai) => {
      for (let y = HORIZON_Y + 190 + ai * 143; y < WORLD_H - 40; y += 330) {
        const s = level.seed * 0.0003 + ax + y;
        const side = jitter(s, 2) > 0.5 ? 1 : -1;
        const x = ax + side * 27;
        if (!clear(x, y, 12, 10)) continue;
        lamps.push({ x, y, side, seed: s });
      }
    });

    trees = [];
    const tryTree = (x, y, size, seed) => {
      if (y < HORIZON_Y + 30 || y > WORLD_H - 20) return;
      if (!clear(x, y, 26, 30)) return;
      for (const l of lamps) if (Math.abs(l.x - x) < 22 && Math.abs(l.y - y) < 30) return;
      trees.push({ x, y, size, seed });
    };
    const rows = Math.max(1, Math.round((WORLD_H - 240) / 200));
    for (let r = 0; r <= rows; r++) {
      for (let k = 0; k < 5; k++) {
        const s = level.seed * 0.001 + r * 17 + k * 3;
        const x = [14, 118, 272, 378, 210][k] + (jitter(s, 1) - 0.5) * 36;
        const y = 150 + r * 200 + (jitter(s, 2) - 0.5) * 170;
        tryTree(x, y, 44 + jitter(s, 3) * 40, s);
      }
    }
    homeWarm.clear();
    collectedStars.clear();
    cockatooHome = null;
    pcount = 0;
    trailN.fill(0);
    trailHead.fill(0);
    camInit = false;
    flashUntil = 0;
  }

  // ---------------------------------------------------------------------
  // Events → effects

  function pushEvents(events, state) {
    if (!events) return;
    for (const ev of events) {
      switch (ev.type) {
        case 'home': {
          if (!homeWarm.has(ev.homeId)) homeWarm.set(ev.homeId, 0.001);
          if (!cockatooHome && state && state.completedHomes.length >= 3) cockatooHome = ev.homeId;
          spawnBurst(ev.x, ev.y, opts.reducedMotion ? 0 : 28, P.particle, 60);
          break;
        }
        case 'star': {
          // Remember which star is gone by position.
          collectedStars.add(`${Math.round(ev.x)}:${Math.round(ev.y)}`);
          spawnBurst(ev.x, ev.y, opts.reducedMotion ? 0 : 12, P.star, 40);
          break;
        }
        case 'burst': {
          spawnBurst(ev.x, ev.y, opts.reducedMotion ? 0 : 60, P.star, ev.radius || 120);
          flashUntil = performance.now() + 220;
          break;
        }
        case 'bell': {
          const hs = houses.find((h) => h.home.id === ev.homeId);
          if (hs) spawnBurst(hs.x, hs.y - hs.h - 30, opts.reducedMotion ? 0 : 20, P.bell, 80);
          break;
        }
        default:
          break;
      }
    }
  }

  function spawnBurst(x, y, n, color, spread) {
    for (let i = 0; i < n && pcount < MAX_PARTICLES; i++) {
      const k = pcount++;
      const a = Math.random() * TAU;
      const sp = 40 + Math.random() * spread;
      px[k] = x; py[k] = y;
      pvx[k] = Math.cos(a) * sp; pvy[k] = Math.sin(a) * sp - 30;
      plife[k] = 0; pmax[k] = 0.6 + Math.random() * 0.6;
      pcol[k] = color;
      psize[k] = 1.5 + Math.random() * 2.5;
    }
  }

  function stepParticles(dt) {
    for (let i = 0; i < pcount; i++) {
      plife[i] += dt;
      if (plife[i] >= pmax[i]) {
        // swap-remove
        pcount--;
        px[i] = px[pcount]; py[i] = py[pcount]; pvx[i] = pvx[pcount]; pvy[i] = pvy[pcount];
        plife[i] = plife[pcount]; pmax[i] = pmax[pcount]; pcol[i] = pcol[pcount]; psize[i] = psize[pcount];
        i--;
        continue;
      }
      pvy[i] += 90 * dt;
      pvx[i] *= 0.985;
      px[i] += pvx[i] * dt;
      py[i] += pvy[i] * dt;
    }
  }

  function drawParticles() {
    for (let i = 0; i < pcount; i++) {
      const a = 1 - plife[i] / pmax[i];
      ctx.fillStyle = withAlpha(pcol[i], Math.round(a * 10) / 10);
      ctx.beginPath();
      ctx.arc(px[i], py[i], psize[i] * (0.6 + a * 0.4), 0, TAU);
      ctx.fill();
    }
  }

  // ---------------------------------------------------------------------
  // Glow sprites

  function glowSprite(color, worldR) {
    const pr = Math.max(4, Math.ceil((worldR * scale * dpr) / 4) * 4);
    const key = `${color}|${pr}`;
    let sp = glowSprites.get(key);
    if (sp) return sp;
    const c = document.createElement('canvas');
    c.width = pr * 2; c.height = pr * 2;
    const g = c.getContext('2d');
    const grad = g.createRadialGradient(pr, pr, 0, pr, pr, pr);
    grad.addColorStop(0, withAlpha(color, 0.75));
    grad.addColorStop(0.35, withAlpha(color, 0.28));
    grad.addColorStop(1, withAlpha(color, 0));
    g.fillStyle = grad;
    g.fillRect(0, 0, pr * 2, pr * 2);
    if (glowSprites.size > 80) glowSprites.clear();
    glowSprites.set(key, c);
    return c;
  }

  function blitGlow(color, x, y, r) {
    const sp = glowSprite(color, r);
    ctx.drawImage(sp, x - r, y - r, r * 2, r * 2);
  }

  // ---------------------------------------------------------------------
  // Background layers

  function drawSky(progress) {
    // Screen-space gradient: the horizon sits at world HORIZON_Y.
    resetTransform();
    const hy = (HORIZON_Y - camY) * scale;
    const top = Math.min(0, hy - ch);
    if (!skyGrad || skyGrad.hy !== hy) {
      const g = ctx.createLinearGradient(0, hy - 900 * scale, 0, hy);
      g.addColorStop(0, P.skyTop);
      g.addColorStop(0.55, P.skyTop);
      g.addColorStop(0.82, P.skyMid);
      g.addColorStop(1, P.skyHorizon);
      skyGrad = { g, hy };
    }
    ctx.fillStyle = P.skyTop;
    ctx.fillRect(0, 0, cw, ch);
    if (hy > 0) {
      ctx.fillStyle = skyGrad.g;
      ctx.fillRect(0, top, cw, Math.max(0, hy - top));
    }
    // Ground below the horizon, warming as the street lights up.
    const ground = mix(P.ground, P.hillFar, Math.min(0.5, progress * 0.5));
    ctx.fillStyle = ground;
    ctx.fillRect(0, Math.max(0, hy), cw, ch);
  }

  function drawHorizon(t, progress) {
    // Stars (only visible at the top), hills, and the apricot afterglow band.
    setTransform();
    // The hills live in the band HORIZON_Y-18 … HORIZON_Y+40, so they are drawn
    // whenever that band is anywhere near the view. Testing camY < HORIZON_Y
    // popped the whole horizon out of existence the instant the camera crossed
    // it, which on the title's slow drift was a visible blink.
    if (camY < HORIZON_Y + 40) {
      ctx.fillStyle = P.stars;
      for (let i = 0; i < 26; i++) {
        const sx = jitter(i, 4) * WORLD_W;
        const sy = jitter(i, 5) * 60;
        const tw = opts.reducedMotion ? 0.7 : 0.5 + 0.5 * Math.sin(t * 0.002 + i);
        ctx.globalAlpha = 0.25 + tw * 0.45 * (1 - sy / 70);
        ctx.beginPath();
        ctx.arc(sx, sy, 0.9 + jitter(i, 6) * 0.8, 0, TAU);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      // far hills
      ctx.fillStyle = P.hillFar;
      ctx.beginPath();
      ctx.moveTo(-10, HORIZON_Y + 4);
      for (let x = -10; x <= WORLD_W + 10; x += 20) {
        const y = HORIZON_Y - 8 - Math.sin(x * 0.021) * 10 - Math.sin(x * 0.07 + 1.3) * 5;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(WORLD_W + 10, HORIZON_Y + 30);
      ctx.lineTo(-10, HORIZON_Y + 30);
      ctx.closePath();
      ctx.fill();
      // near hills with a handful of distant gums
      ctx.fillStyle = P.hillNear;
      ctx.beginPath();
      ctx.moveTo(-10, HORIZON_Y + 30);
      for (let x = -10; x <= WORLD_W + 10; x += 16) {
        const y = HORIZON_Y + 2 - Math.sin(x * 0.03 + 2) * 6 - Math.sin(x * 0.11) * 2;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(WORLD_W + 10, HORIZON_Y + 40);
      ctx.lineTo(-10, HORIZON_Y + 40);
      ctx.closePath();
      ctx.fill();
      for (let i = 0; i < 9; i++) {
        const x = 20 + i * 44 + jitter(i, 9) * 20;
        drawGumTree(ctx, P, x, HORIZON_Y + 4 - Math.sin(x * 0.03 + 2) * 6, 18 + jitter(i, 12) * 12, i + 40, 0);
      }
      drawFarSuburb(progress);
    }
    // Afterglow below the horizon. It reaches a long way down the street: the
    // apricot bleed is what makes the first third of the world read as dusk
    // rather than as night, and 90 units of it were invisible by row two.
    const g = ctx.createLinearGradient(0, HORIZON_Y, 0, HORIZON_Y + 520);
    g.addColorStop(0, withAlpha(P.skyHorizon, 0.16 + progress * 0.07));
    g.addColorStop(0.2, withAlpha(P.skyHorizon, 0.06 + progress * 0.04));
    g.addColorStop(1, withAlpha(P.skyHorizon, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, HORIZON_Y, WORLD_W, 520);
  }

  /**
   * The rest of the suburb, along the horizon. The world's first cross street
   * is 300 units down, so without this the top of every street is a wide band
   * of empty ground between the hills and the first row of homes.
   */
  function drawFarSuburb(progress) {
    // Two staggered rows: the far one small and hazed into the hills, the near
    // one bigger, so the eye reads distance rather than a paper cut-out.
    const rows = [
      { base: HORIZON_Y + 44, n: 24, step: 17.5, scale: 0.8, salt: 31, mixT: 0.55 },
      { base: HORIZON_Y + 88, n: 18, step: 23, scale: 1.15, salt: 41, mixT: 0.85 },
    ];
    for (const row of rows) {
      ctx.fillStyle = mix(P.hillNear, P.filler, row.mixT);
      for (let i = 0; i < row.n; i++) {
        const x = -14 + i * row.step + jitter(i, row.salt) * 9;
        const w = (14 + jitter(i, row.salt + 1) * 11) * row.scale;
        const h = (10 + jitter(i, row.salt + 2) * 9) * row.scale;
        const y = row.base - jitter(i, row.salt + 3) * 8;
        ctx.fillRect(x - w / 2, y - h, w, h);
        ctx.beginPath();
        ctx.moveTo(x - w / 2 - 2, y - h + 1);
        ctx.lineTo(x, y - h - h * 0.44);
        ctx.lineTo(x + w / 2 + 2, y - h + 1);
        ctx.closePath();
        ctx.fill();
      }
      // A scatter of far-away windows already on. Nothing here is playable; it
      // is the promise that this street is one of many.
      for (let i = 0; i < row.n; i++) {
        if (jitter(i, row.salt + 4) > 0.32 + progress * 0.32) continue;
        const x = -14 + i * row.step + jitter(i, row.salt) * 9;
        const y = row.base - jitter(i, row.salt + 3) * 8 - 5 * row.scale;
        ctx.fillStyle = withAlpha(P.windowGlow, 0.4 + progress * 0.3);
        ctx.fillRect(x - 1.6, y - 2, 3.2, 3);
      }
    }
    // Ground haze, so the far suburb sits behind the near street.
    const g = ctx.createLinearGradient(0, HORIZON_Y + 10, 0, HORIZON_Y + 118);
    g.addColorStop(0, withAlpha(P.skyHorizon, 0));
    g.addColorStop(1, withAlpha(P.skyHorizon, 0.11));
    ctx.fillStyle = g;
    ctx.fillRect(0, HORIZON_Y + 10, WORLD_W, 108);
  }

  /**
   * Depth, in screen space. A touch of the sky's warmth at the top of the view
   * and a soft vignette at the edges, both always present, so the picture has
   * air in it even four screens below the horizon where no sky is drawn.
   */
  function drawAtmosphere(progress) {
    resetTransform();
    const depth = Math.min(1, Math.max(0, (camY - HORIZON_Y) / 900));
    const g = ctx.createLinearGradient(0, 0, 0, ch * 0.6);
    g.addColorStop(0, withAlpha(P.skyGlow, (0.09 - depth * 0.05) + progress * 0.05));
    g.addColorStop(1, withAlpha(P.skyGlow, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cw, ch * 0.6);
    if (!vignette || vignette.w !== cw || vignette.h !== ch) {
      const v = ctx.createRadialGradient(cw / 2, ch * 0.42, Math.min(cw, ch) * 0.45, cw / 2, ch * 0.42, Math.max(cw, ch) * 0.85);
      v.addColorStop(0, withAlpha(P.skyTop, 0));
      v.addColorStop(1, withAlpha(P.skyTop, 0.32));
      vignette = { g: v, w: cw, h: ch };
    }
    ctx.fillStyle = vignette.g;
    ctx.fillRect(0, 0, cw, ch);
  }

  const ROAD_W = 44;
  const CROSS_W = 34;

  function nearestAvenue(x) {
    let best = avenues[0] ?? x;
    for (const a of avenues) if (Math.abs(a - x) < Math.abs(best - x)) best = a;
    return best;
  }

  /** Only what the camera can see: the world is four screens tall. */
  function inView(y, pad = 120) {
    return y > camY - pad && y < camY + viewH() + pad;
  }

  function drawGround(progress) {
    // The avenues start just above the first cross street, not at the horizon.
    // A road that runs all the way up into the hills has no vanishing point to
    // give it, so it reads as a column standing in front of them.
    const top = Math.max(HORIZON_Y + 30, (crossStreets[0] ?? HORIZON_Y + 200) - 96);
    // Nature strip: a thin band of grass between kerb and fence line. Kept
    // narrow and dark — a wide pale verge turned the street into a blueprint.
    ctx.fillStyle = withAlpha(P.verge, 0.34);
    for (const x of avenues) ctx.fillRect(x - ROAD_W / 2 - 8, top, ROAD_W + 16, WORLD_H - top);

    // Asphalt. Cross streets first so the avenues read as running over them.
    ctx.fillStyle = mix(P.road, P.skyMid, progress * 0.1);
    for (const y of crossStreets) {
      if (!inView(y)) continue;
      ctx.fillRect(0, y - CROSS_W / 2, WORLD_W, CROSS_W);
    }
    for (const x of avenues) ctx.fillRect(x - ROAD_W / 2, top, ROAD_W, WORLD_H - top);

    // Kerbs. A pale line each side is the whole reason a road reads as a road.
    ctx.strokeStyle = withAlpha(P.kerb, 0.55);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (const x of avenues) {
      ctx.moveTo(x - ROAD_W / 2, top); ctx.lineTo(x - ROAD_W / 2, WORLD_H);
      ctx.moveTo(x + ROAD_W / 2, top); ctx.lineTo(x + ROAD_W / 2, WORLD_H);
    }
    ctx.stroke();
    // Cross streets get a kerb only where they are not already under an
    // avenue, so the intersections stay open instead of drawing a ladder.
    ctx.strokeStyle = withAlpha(P.kerb, 0.45);
    ctx.beginPath();
    for (const y of crossStreets) {
      if (!inView(y)) continue;
      let x0 = 0;
      for (const ax of [...avenues, WORLD_W + 40]) {
        const x1 = ax - ROAD_W / 2 - 8;
        if (x1 > x0 + 6) {
          ctx.moveTo(x0, y - CROSS_W / 2); ctx.lineTo(x1, y - CROSS_W / 2);
          ctx.moveTo(x0, y + CROSS_W / 2); ctx.lineTo(x1, y + CROSS_W / 2);
        }
        x0 = ax + ROAD_W / 2 + 8;
      }
    }
    ctx.stroke();

    // Centre lines.
    ctx.strokeStyle = withAlpha(P.kerb, 0.26);
    ctx.lineWidth = 1.2;
    ctx.setLineDash([11, 18]);
    ctx.beginPath();
    for (const x of avenues) { ctx.moveTo(x, top + 10); ctx.lineTo(x, WORLD_H); }
    ctx.stroke();
    ctx.setLineDash([]);

    // The avenues dissolve into the haze at the far end instead of butting
    // into the hills. Tier 2 and 3 streets start only ~27 units below the
    // horizon, so there is no room for a vanishing point — this stands in for
    // one, and it is what stops three roads reading as three columns.
    const fadeH = 78;
    const fade = ctx.createLinearGradient(0, top - 4, 0, top + fadeH);
    fade.addColorStop(0, mix(P.ground, P.skyHorizon, 0.14));
    fade.addColorStop(1, withAlpha(P.ground, 0));
    ctx.fillStyle = fade;
    for (const x of avenues) ctx.fillRect(x - ROAD_W / 2 - 10, top - 4, ROAD_W + 20, fadeH + 4);

    // Front fences, one short run per background house. Drawn as runs rather
    // than as continuous lines down the verge: six full-height dashed lines
    // plus six kerbs plus three centre lines is fifteen vertical strokes, and
    // the street stopped being a street and became a ruled page.
    ctx.strokeStyle = withAlpha(P.fence, 0.85);
    ctx.lineWidth = 1.4;
    ctx.setLineDash([2.5, 4]);
    ctx.beginPath();
    for (const f of fillers) {
      if (!inView(f.y, 40)) continue;
      const lane = nearestAvenue(f.x);
      const fx = lane + Math.sign(f.x - lane || 1) * (ROAD_W / 2 + 11);
      ctx.moveTo(fx, f.y - f.h * 0.9);
      ctx.lineTo(fx, f.y + f.h * 0.5);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    // Front lawns, warming as the street comes on.
    ctx.fillStyle = withAlpha(P.treeLit, 0.4 + progress * 0.22);
    for (const l of lawns) {
      if (!inView(l.y)) continue;
      ctx.beginPath();
      ctx.ellipse(l.x, l.y, l.w / 2, l.h / 2, 0, 0, TAU);
      ctx.fill();
    }
  }

  /** Background houses: the rest of the neighbourhood, never lit. */
  function drawFillers() {
    ctx.lineJoin = 'round';
    // Scenery recedes: the further a house sits back from its street, the less
    // of it the eye should get. Otherwise the background out-shouts the homes
    // the round is actually about.
    ctx.globalAlpha = 0.82;
    for (const f of fillers) {
      if (!inView(f.y)) continue;
      ctx.fillStyle = P.filler;
      roundRect(ctx, f.x - f.w / 2, f.y - f.h, f.w, f.h, 2);
      ctx.fill();
      ctx.fillStyle = P.fillerRoof;
      ctx.beginPath();
      if (f.variant === 2) {
        // flat roof
        roundRect(ctx, f.x - f.w / 2 - 3, f.y - f.h - 4, f.w + 6, 5, 1);
      } else {
        ctx.moveTo(f.x - f.w / 2 - 4, f.y - f.h + 1);
        ctx.lineTo(f.x, f.y - f.h - f.h * (f.variant ? 0.34 : 0.46));
        ctx.lineTo(f.x + f.w / 2 + 4, f.y - f.h + 1);
        ctx.closePath();
      }
      ctx.fill();
      // A dark window on some of them. On all of them it read as wallpaper:
      // forty identical pale slabs down the street.
      if (f.variant === 1) {
        ctx.fillStyle = withAlpha(P.windowDark, 0.7);
        roundRect(ctx, f.x - f.w * 0.18, f.y - f.h * 0.6, f.w * 0.36, f.h * 0.28, 1.5);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }

  /**
   * Street lamps. A warm pool on the asphalt says "dusk, and the council
   * lights are already on" better than any amount of sky. Deliberately no
   * light cone: a solid triangle of light per lamp read as a row of tents.
   */
  function drawLamps(progress) {
    for (const l of lamps) {
      if (!inView(l.y)) continue;
      const armX = l.x - l.side * 15;
      const headY = l.y - 40;
      // The pool first, on the ground, so the pole stands in it.
      const spread = 30;
      const g = ctx.createRadialGradient(armX, l.y + 2, 1, armX, l.y + 2, spread);
      g.addColorStop(0, withAlpha(P.lampGlow, 0.16 + progress * 0.06));
      g.addColorStop(1, withAlpha(P.lampGlow, 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(armX, l.y + 2, spread, spread * 0.45, 0, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = P.lamp;
      ctx.lineWidth = 2.4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(l.x, l.y);
      ctx.lineTo(l.x, headY + 6);
      ctx.quadraticCurveTo(l.x, headY, armX, headY);
      ctx.stroke();
      blitGlow(P.lampGlow, armX, headY + 1, 13);
      ctx.fillStyle = P.lampGlow;
      roundRect(ctx, armX - 4, headY - 1, 8, 3.2, 1.5);
      ctx.fill();
    }
  }

  // ---------------------------------------------------------------------
  // The street proper

  function homeLit(homeId) {
    return homeWarm.get(homeId) ?? 0;
  }

  function stepWarmth(dt) {
    for (const [id, v] of homeWarm) {
      if (v < 1) homeWarm.set(id, Math.min(1, v + (opts.reducedMotion ? 1 : dt / 0.6)));
    }
  }

  function drawTrees(progress) {
    for (const tr of trees) if (inView(tr.y, 80)) drawGumTree(ctx, P, tr.x, tr.y, tr.size, tr.seed, progress);
  }

  function drawHouses(t) {
    for (const hs of houses) {
      if (!inView(hs.y, 120)) continue;
      const lit = homeLit(hs.home.id);
      drawHouse(ctx, P, {
        x: hs.x, y: hs.y, w: hs.w, h: hs.h, variant: hs.variant, lit, community: hs.community,
        cockatoo: cockatooHome === hs.home.id, seed: hs.index + (lvl?.seed ?? 0),
      });
      if (lit > 0.6) {
        // Moment icon over the roof.
        const rise = (lit - 0.6) / 0.4;
        drawMomentIcon(ctx, P, hs.home.moment, hs.x, hs.y - hs.h - (hs.community ? 62 : 44) - rise * 6, 11);
      }
      if (t && lit > 0.05) {
        // A little heat-shimmer sparkle at the eave.
        ctx.fillStyle = withAlpha(P.windowGlow, 0.25 * lit);
        ctx.beginPath();
        ctx.ellipse(hs.x, hs.y - 2, hs.w * 0.7, 6, 0, 0, TAU);
        ctx.fill();
      }
    }
  }

  function cableWidth(edge) {
    const base = edge.golden ? 3.2 : 2.4;
    return opts.highContrast ? base * 2 : base;
  }

  function drawCables(state, approachingSet) {
    ctx.lineCap = 'round';
    for (const g of edgeGeom) {
      if (!inView(Math.min(g.ay, g.by), 60) && !inView(Math.max(g.ay, g.by), 60)) continue;
      const e = g.edge;
      const litEdge = e.homeId && homeLit(e.homeId) > 0.5;
      ctx.strokeStyle = e.golden ? P.gold : e.tangled ? P.tangle : litEdge ? mix(P.cable, P.cableActive, 0.5) : P.cable;
      ctx.lineWidth = cableWidth(e);
      ctx.beginPath();
      ctx.moveTo(g.ax, g.ay);
      ctx.lineTo(g.bx, g.by);
      ctx.stroke();
      if (e.tangled) {
        const mx = (g.ax + g.bx) / 2; const my = (g.ay + g.by) / 2;
        drawTangle(ctx, P, mx + g.nx * 2, my + g.ny * 2, 9);
      }
    }
    // Active-out edges from every junction get a small chevron so the route
    // can be read ahead; the APPROACHING junction's gets the full treatment.
    const switches = state?.switches;
    for (const n of lvl.nodes) {
      if (n.kind !== 'junction' && n.kind !== 'split') continue;
      if (!inView(n.y, 80)) continue;
      const eid = n.out[switches ? (switches[n.id] ?? 0) : (n.switchIndex ?? 0)] ?? n.out[0];
      const g = geomById.get(eid);
      if (!g) continue;
      const approaching = approachingSet.has(n.id);
      if (approaching) {
        const w = cableWidth(g.edge);
        ctx.strokeStyle = P.white;
        ctx.lineWidth = w + (opts.highContrast ? 7 : 4.5);
        ctx.beginPath(); ctx.moveTo(g.ax, g.ay); ctx.lineTo(g.bx, g.by); ctx.stroke();
        ctx.strokeStyle = P.cableActive;
        ctx.lineWidth = w + 1;
        ctx.beginPath(); ctx.moveTo(g.ax, g.ay); ctx.lineTo(g.bx, g.by); ctx.stroke();
        // three chevrons marching along the route
        const phase = opts.reducedMotion ? 0 : ((state?.elapsedMs ?? 0) % 900) / 900;
        for (let k = 0; k < 3; k++) {
          const f = (0.2 + k * 0.27 + phase * 0.27) % 1;
          if (f < 0.12 || f > 0.92) continue;
          chevron(g.ax + g.ux * g.len * f, g.ay + g.uy * g.len * f, g.ux, g.uy, opts.highContrast ? 9 : 7, P.white);
        }
      } else {
        // Close to the junction it belongs to: at a third of the way down the
        // cable it read as litter floating in the middle of the street.
        chevron(g.ax + g.ux * 22, g.ay + g.uy * 22, g.ux, g.uy, 4.5, withAlpha(P.cableActive, 0.6));
      }
    }
  }

  function chevron(x, y, ux, uy, s, color) {
    ctx.strokeStyle = color;
    ctx.lineWidth = opts.highContrast ? 3.4 : 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const nx = -uy; const ny = ux;
    ctx.beginPath();
    ctx.moveTo(x - ux * s + nx * s, y - uy * s + ny * s);
    ctx.lineTo(x, y);
    ctx.lineTo(x - ux * s - nx * s, y - uy * s - ny * s);
    ctx.stroke();
  }

  function drawBulbs(state, t) {
    const lit = state?.litBulbs;
    const r = opts.highContrast ? 4.4 : 3.6;
    for (const g of edgeGeom) {
      const e = g.edge;
      if (!e.bulbs.length) continue;
      if (!inView(Math.min(g.ay, g.by), 60) && !inView(Math.max(g.ay, g.by), 60)) continue;
      const hang = 6;
      for (let i = 0; i < e.bulbs.length; i++) {
        const b = e.bulbs[i];
        // bulbs hang below a horizontal cable, beside a vertical one
        const bx = b.x + (g.horizontal ? 0 : g.nx * hang);
        const by = b.y + (g.horizontal ? hang : g.ny * hang);
        const on = lit ? lit.has(`${e.id}:${i}`) : false;
        // the little wire from cable to bulb
        ctx.strokeStyle = on ? withAlpha(P.cableActive, 0.8) : withAlpha(P.cable, 0.9);
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.lineTo(bx, by); ctx.stroke();
        if (on) {
          const col = P.bulbs[(g.colorBase + i) % 5];
          blitGlow(col, bx, by, r * 3.4);
          ctx.fillStyle = col;
          ctx.beginPath(); ctx.arc(bx, by, r, 0, TAU); ctx.fill();
          ctx.fillStyle = withAlpha(P.white, 0.7);
          ctx.beginPath(); ctx.arc(bx - r * 0.3, by - r * 0.35, r * 0.3, 0, TAU); ctx.fill();
        } else {
          // An unlit bulb still has to read as a bulb waiting to come on: it
          // is the promise the round is about. A rim and a dot of glass.
          ctx.fillStyle = mix(P.bulbOff, P.cable, 0.35);
          ctx.beginPath(); ctx.arc(bx, by, r * 0.9, 0, TAU); ctx.fill();
          ctx.strokeStyle = withAlpha(P.cableActive, 0.3);
          ctx.lineWidth = 0.9;
          ctx.stroke();
          ctx.fillStyle = withAlpha(P.white, 0.16);
          ctx.beginPath(); ctx.arc(bx - r * 0.28, by - r * 0.3, r * 0.26, 0, TAU); ctx.fill();
        }
      }
      if (e.star) {
        const key = `${Math.round(e.star.x)}:${Math.round(e.star.y)}`;
        if (!collectedStars.has(key)) drawStar(ctx, P, e.star.x + g.nx * 14, e.star.y + g.ny * 14, 9, t);
      }
    }
  }

  /** The pole a cable hangs from: every node is one. */
  function drawPole(x, y, h = 16) {
    ctx.strokeStyle = withAlpha(P.lamp, 0.9);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(x, y + 1); ctx.lineTo(x, y + h); ctx.stroke();
  }

  /**
   * Junctions. ONE of these is the decision the player is about to make and
   * the rest are furniture, so they are drawn as two different objects rather
   * than as the same object at two opacities: an idle junction is a small ring
   * with an arrow, the approaching one is a big pulsing orange target. It used
   * to be a 56-unit pale disc with a white teardrop hub at every node, which
   * read as a screenful of map pins.
   */
  function drawJunctions(state, approaching, t, chrome = true) {
    const sparks = state?.sparks ?? [];
    const switches = state?.switches;
    for (const n of lvl.nodes) {
      if (!inView(n.y, 80)) continue;
      if (n.kind === 'bauble') {
        const parked = sparks.find((s) => s.parked && Math.abs(s.x - n.x) < 2 && Math.abs(s.y - n.y) < 2);
        drawBauble(ctx, P, n.x, n.y, 13, parked ? (parked.charge ?? 1) : 0, opts.reducedMotion ? 0 : t);
        continue;
      }
      if (n.kind !== 'junction' && n.kind !== 'split') {
        drawPole(n.x, n.y, 14);
        ctx.fillStyle = P.cable;
        ctx.beginPath(); ctx.arc(n.x, n.y, 3, 0, TAU); ctx.fill();
        continue;
      }
      drawPole(n.x, n.y, 18);
      const eid = n.out[switches ? (switches[n.id] ?? 0) : (n.switchIndex ?? 0)] ?? n.out[0];
      const g = geomById.get(eid);
      const ap = chrome && approaching.some((a) => a.nodeId === n.id);

      if (!ap) {
        // Idle: a quiet ring you could tap, with the live way out marked.
        const r = 13;
        ctx.fillStyle = withAlpha(P.skyTop, 0.5);
        ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, TAU); ctx.fill();
        ctx.strokeStyle = withAlpha(P.junction, opts.highContrast ? 0.7 : 0.42);
        ctx.lineWidth = opts.highContrast ? 2.4 : 1.3;
        ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, TAU); ctx.stroke();
        ctx.fillStyle = n.kind === 'split' ? P.bulb4 : withAlpha(P.junction, 0.8);
        ctx.beginPath(); ctx.arc(n.x, n.y, 3.4, 0, TAU); ctx.fill();
        // No chevron here: drawCables already marks every junction's live way
        // out, and two chevron systems drew a stutter of arrows at each ring.
        continue;
      }

      // Approaching: the decision. Big, orange, pulsing, unmistakable.
      const pulse = opts.reducedMotion ? 1 : 1 + Math.sin(t * 0.011) * 0.07;
      const r = JUNCTION_R * pulse;
      if (!opts.reducedMotion) blitGlow(P.junctionRing, n.x, n.y, r * 1.9);
      ctx.fillStyle = withAlpha(P.skyTop, 0.55);
      ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, TAU); ctx.fill();
      ctx.strokeStyle = P.junctionRing;
      ctx.lineWidth = opts.highContrast ? 6 : 4;
      ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, TAU); ctx.stroke();
      // The white inner ring is the non-colour signal: high contrast, and
      // legible to anyone who cannot separate the orange from the ground.
      ctx.strokeStyle = withAlpha(P.white, 0.95);
      ctx.lineWidth = opts.highContrast ? 3 : 1.8;
      ctx.beginPath(); ctx.arc(n.x, n.y, r - (opts.highContrast ? 6 : 4.5), 0, TAU); ctx.stroke();
      ctx.fillStyle = n.kind === 'split' ? P.bulb4 : P.white;
      ctx.beginPath(); ctx.arc(n.x, n.y, 4.5, 0, TAU); ctx.fill();
      if (g) {
        // A clean arrowhead down the live route, clear of the hub.
        ctx.fillStyle = P.junctionRing;
        ctx.beginPath();
        ctx.moveTo(n.x + g.ux * 19, n.y + g.uy * 19);
        ctx.lineTo(n.x + g.ux * 9 + g.nx * 6, n.y + g.uy * 9 + g.ny * 6);
        ctx.lineTo(n.x + g.ux * 9 - g.nx * 6, n.y + g.uy * 9 - g.ny * 6);
        ctx.closePath();
        ctx.fill();
      }
      if (n.kind === 'split') {
        ctx.fillStyle = P.skyTop;
        ctx.beginPath(); ctx.arc(n.x - 2.5, n.y, 1.8, 0, TAU); ctx.arc(n.x + 2.5, n.y, 1.8, 0, TAU); ctx.fill();
      }
    }
  }

  function pushTrail(i, x, y) {
    const h = trailHead[i];
    trailX[i][h] = x; trailY[i][h] = y;
    trailHead[i] = (h + 1) % TRAIL_LEN;
    if (trailN[i] < TRAIL_LEN) trailN[i]++;
  }

  function drawSparks(state, t) {
    const sparks = state?.sparks ?? [];
    for (let i = 0; i < sparks.length && i < MAX_SPARKS; i++) {
      const s = sparks[i];
      if (!opts.reducedMotion) pushTrail(i, s.x, s.y);
      // trail
      if (!opts.reducedMotion && trailN[i] > 1) {
        const n = trailN[i];
        for (let k = 1; k < n; k++) {
          const idx = (trailHead[i] - 1 - k + TRAIL_LEN * 2) % TRAIL_LEN;
          const a = 1 - k / n;
          ctx.fillStyle = withAlpha(P.sparkGlow, Math.round(a * 0.6 * 10) / 10);
          ctx.beginPath();
          ctx.arc(trailX[i][idx], trailY[i][idx], 6 * a + 1, 0, TAU);
          ctx.fill();
        }
      }
      const boost = s.speedMul > 1.1 ? 1.25 : 1;
      const pulse = opts.reducedMotion ? 1 : 1 + Math.sin(t * 0.02) * 0.08;
      blitGlow(P.spark, s.x, s.y, 34 * boost * pulse);
      ctx.fillStyle = P.spark;
      ctx.beginPath(); ctx.arc(s.x, s.y, 8 * boost, 0, TAU); ctx.fill();
      ctx.fillStyle = P.white;
      ctx.beginPath(); ctx.arc(s.x, s.y, 4 * boost, 0, TAU); ctx.fill();
      if (s.isClone) {
        ctx.strokeStyle = P.bulb4;
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(s.x, s.y, 12, 0, TAU); ctx.stroke();
      }
    }
    for (let i = sparks.length; i < MAX_SPARKS; i++) trailN[i] = 0;
  }

  // ---------------------------------------------------------------------
  // The round

  let lastT = 0;
  let camScaleTarget = null;

  /**
   * Draw one frame of the round.
   * @param level  the Level (setLevel is called if it changed)
   * @param state  sim.state
   * @param fx     { now, approaching:[{nodeId,sparkIndex}], reveal: 0..1 }
   * @param cameraY optional override of the camera top, in world units
   */
  function draw(level, state, fx = {}, cameraY = null) {
    if (level !== lvl) setLevel(level);
    const now = fx.now ?? performance.now();
    const dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 0.016;
    lastT = now;
    const approaching = fx.approaching ?? [];
    const approachingSet = new Set(approaching.map((a) => a.nodeId));
    const total = state?.homesTotal || level.homes.length || 1;
    const progress = (state?.completedHomes?.length ?? 0) / total;

    // Camera: follow the lead spark; pull back over the reveal.
    const reveal = fx.reveal ?? 0;
    const fitScale = Math.min(cw / WORLD_W, ch / WORLD_H);
    const lead = state?.sparks?.[0];
    if (reveal > 0) {
      const k = opts.reducedMotion ? 1 : easeInOut(reveal);
      scale = followScale + (fitScale - followScale) * k;
      const targetY = 0;
      const fromY = camScaleTarget ?? camY;
      if (camScaleTarget === null) camScaleTarget = camY;
      camY = fromY + (targetY - fromY) * k;
      camX = -((cw / scale - WORLD_W) / 2);
    } else {
      scale = followScale;
      camX = 0;
      camScaleTarget = null;
      let target = camY;
      if (cameraY !== null && cameraY !== undefined) target = cameraY;
      else if (lead) target = lead.y - viewH() * 0.42;
      target = clampCam(target);
      if (!camInit || opts.reducedMotion) { camY = target; camInit = true; } else camY += (target - camY) * Math.min(1, dt * 5);
    }

    stepWarmth(dt);
    if (!opts.reducedMotion) stepParticles(dt);

    drawSky(progress);
    drawHorizon(now, progress);
    setTransform();
    drawGround(progress);
    drawLamps(progress);
    drawFillers();
    drawTrees(progress);
    drawHouses(now);
    drawCables(state, approachingSet);
    drawBulbs(state, now);
    drawJunctions(state, approaching, now);
    drawSparks(state, now);
    drawParticles();
    // Screen space from here down.
    drawAtmosphere(progress);
    if (flashUntil > now && !opts.reducedMotion) {
      ctx.fillStyle = withAlpha(P.star, Math.round(((flashUntil - now) / 220) * 0.35 * 10) / 10);
      ctx.fillRect(0, 0, cw, ch);
    }
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2;
  }

  // ---------------------------------------------------------------------
  // Ambient (title hero): the street, everything lit and twinkling, a spark
  // drifting along the backbone.

  let ambientState = null;

  function drawAmbient(level, t) {
    if (level !== lvl) { setLevel(level); ambientState = null; }
    if (!ambientState) {
      const litBulbs = new Set();
      for (const e of level.edges) for (let i = 0; i < e.bulbs.length; i++) litBulbs.add(`${e.id}:${i}`);
      ambientState = {
        litBulbs, sparks: [{ x: 0, y: 0, edgeId: null, t: 0, speedMul: 1, parked: false, isClone: false }],
        completedHomes: level.homes.map((h) => h.id), homesTotal: level.homes.length, elapsedMs: 0,
        switches: Object.fromEntries(level.nodes.map((n) => [n.id, n.switchIndex ?? 0])),
      };
      for (const h of level.homes) homeWarm.set(h.id, 1);
    }
    // A spark wandering the backbone, following the switch state (cycling it).
    const s = ambientState.sparks[0];
    const speed = opts.reducedMotion ? 0 : 70;
    if (!s.edgeId) { s.edgeId = level.start.edgeId; s.t = 0; s.last = t; }
    const dt = Math.min(0.05, (t - (s.last ?? t)) / 1000);
    s.last = t;
    let g = geomById.get(s.edgeId);
    if (g) {
      s.t += (speed * dt) / g.len;
      while (s.t >= 1 && g) {
        s.t -= 1;
        const node = nodeById.get(g.edge.to);
        const idx = Math.floor(jitter(Math.floor(t / 1000) + node.x, 3) * node.out.length);
        ambientState.switches[node.id] = idx;
        s.edgeId = node.out[idx] ?? node.out[0];
        g = geomById.get(s.edgeId);
      }
      if (g) { s.x = g.ax + g.ux * g.len * s.t; s.y = g.ay + g.uy * g.len * s.t; }
    }
    ambientState.elapsedMs = t;

    // Twinkle: flip a handful of bulbs off and on with time.
    const lit = ambientState.litBulbs;
    if (!opts.reducedMotion) {
      let k = 0;
      for (const e of level.edges) {
        for (let i = 0; i < e.bulbs.length; i++, k++) {
          const on = Math.sin(t * 0.0016 + k * 1.7) > -0.85;
          const key = `${e.id}:${i}`;
          if (on) lit.add(key); else lit.delete(key);
        }
      }
    }

    // Camera: frame the apricot horizon AND the first row of homes at once.
    // Holding the plain top of the world put two thirds of the hero on empty
    // ground above the first cross street — a picture of nothing much.
    // Frame the apricot horizon AND the first row of homes at once: holding
    // the plain top of the world put two thirds of the hero on empty ground.
    const firstRow = crossStreets[0] ?? HORIZON_Y + 210;
    const wanted = firstRow + 132 - (HORIZON_Y - 42);
    // Fill the frame rather than fit inside it. Fitting left a wide desktop
    // hero with two bands of empty ground either side of the street; cropping
    // a little off the bottom of the near houses is the cheaper loss.
    scale = Math.max(cw / (WORLD_W + 26), ch / wanted);
    camX = -((cw / scale - WORLD_W) / 2);
    const drift = opts.reducedMotion ? 0 : (Math.sin(t * 0.00014) * 0.5 + 0.5) * 22;
    camY = HORIZON_Y - 42 + drift;
    camInit = true;

    drawSky(1);
    drawHorizon(t, 1);
    setTransform();
    drawGround(1);
    drawLamps(1);
    drawFillers();
    drawTrees(1);
    drawHouses(t);
    drawCables(ambientState, new Set());
    drawBulbs(ambientState, t);
    // `chrome: false` — the title's street is a picture, not a playfield, so
    // it gets poles and baubles but no junction targets to tap.
    drawJunctions(ambientState, [], t, false);
    drawSparks(ambientState, t);
    drawAtmosphere(1);
    resetTransform();
  }

  // ---------------------------------------------------------------------
  // My Street: a row of homes with ten decoration slots.

  const SLOTS = [
    // [x, y, kind] in a 390 × 260 scene; kind hints where the slot sits
    [58, 132, 'roof'], [150, 118, 'roof'], [250, 128, 'roof'], [342, 122, 'roof'],
    [20, 210, 'yard'], [104, 214, 'yard'], [200, 216, 'yard'], [296, 212, 'yard'], [372, 214, 'yard'],
    [200, 62, 'sky'],
  ];

  function drawMyStreet(placed, t) {
    const SCENE_H = 260;
    scale = cw / WORLD_W;
    const sceneScale = Math.min(scale, ch / SCENE_H);
    scale = sceneScale;
    camX = -((cw / scale - WORLD_W) / 2);
    camY = -((ch / scale - SCENE_H) / 2);
    resetTransform();
    ctx.fillStyle = P.skyTop;
    ctx.fillRect(0, 0, cw, ch);
    setTransform();
    // sky
    const g = ctx.createLinearGradient(0, 0, 0, 150);
    g.addColorStop(0, P.skyTop);
    g.addColorStop(0.6, P.skyMid);
    g.addColorStop(1, P.skyHorizon);
    ctx.fillStyle = g;
    ctx.fillRect(-400, -400, WORLD_W + 800, 550);
    ctx.fillStyle = P.stars;
    for (let i = 0; i < 20; i++) {
      ctx.globalAlpha = 0.3 + 0.4 * (opts.reducedMotion ? 0.6 : 0.5 + 0.5 * Math.sin(t * 0.002 + i));
      ctx.beginPath(); ctx.arc(jitter(i, 21) * WORLD_W, jitter(i, 22) * 70, 1, 0, TAU); ctx.fill();
    }
    ctx.globalAlpha = 1;
    // hills + ground
    ctx.fillStyle = P.hillFar;
    ctx.beginPath();
    ctx.moveTo(-400, 160);
    for (let x = -400; x <= WORLD_W + 400; x += 20) ctx.lineTo(x, 140 - Math.sin(x * 0.02) * 10 - Math.sin(x * 0.07) * 4);
    ctx.lineTo(WORLD_W + 400, 200); ctx.lineTo(-400, 200); ctx.closePath(); ctx.fill();
    ctx.fillStyle = P.ground;
    ctx.fillRect(-400, 150, WORLD_W + 800, 400);
    ctx.fillStyle = P.road;
    roundRect(ctx, -400, 232, WORLD_W + 800, 40, 0); ctx.fill();
    ctx.strokeStyle = withAlpha(P.cable, 0.4); ctx.lineWidth = 1.2; ctx.setLineDash([10, 14]);
    ctx.beginPath(); ctx.moveTo(-400, 252); ctx.lineTo(WORLD_W + 400, 252); ctx.stroke(); ctx.setLineDash([]);
    // trees
    drawGumTree(ctx, P, 12, 178, 56, 3, 1);
    drawGumTree(ctx, P, 300, 174, 48, 5, 1);
    drawGumTree(ctx, P, 386, 182, 60, 6, 1);
    // houses
    const homes = [[58, 190, 60, 42, 0], [150, 188, 66, 44, 2], [250, 190, 62, 42, 1], [342, 188, 58, 40, 3]];
    homes.forEach(([x, y, w, h, v], i) => drawHouse(ctx, P, { x, y, w, h, variant: v, lit: 1, seed: i }));
    // a cable across the street with lit bulbs
    ctx.strokeStyle = P.cable; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(-10, 150); ctx.quadraticCurveTo(195, 168, 400, 150); ctx.stroke();
    for (let i = 0; i < 18; i++) {
      const f = (i + 0.5) / 18;
      const x = -10 + 410 * f;
      const y = 150 + 4 * (1 - (2 * f - 1) ** 2) * 4.5 + 5;
      const col = P.bulbs[i % 5];
      const on = opts.reducedMotion || Math.sin(t * 0.002 + i * 1.3) > -0.7;
      if (on) { blitGlow(col, x, y, 10); ctx.fillStyle = col; } else ctx.fillStyle = P.bulbOff;
      ctx.beginPath(); ctx.arc(x, y, 3, 0, TAU); ctx.fill();
    }
    // slots
    SLOTS.forEach(([x, y], i) => {
      const id = placed?.[String(i)] ?? placed?.[i];
      if (id) {
        drawDecoration(ctx, P, id, x, y, 30, t);
      } else {
        ctx.setLineDash([3, 4]);
        ctx.strokeStyle = withAlpha(P.junction, 0.45);
        ctx.lineWidth = 1.2;
        ctx.beginPath(); ctx.arc(x, y, 13, 0, TAU); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = withAlpha(P.junction, 0.7);
        ctx.font = '700 10px system-ui, sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(String(i + 1), x, y);
      }
    });
    drawCockatoo(ctx, P, 214, 152, 0.9);
    resetTransform();
  }

  // ---------------------------------------------------------------------
  // Australia: the outline, town lights by milestone.

  function drawAustralia(total, milestone, t) {
    resetTransform();
    ctx.fillStyle = P.skyTop;
    ctx.fillRect(0, 0, cw, ch);
    const pad = 16;
    const bw = cw - pad * 2;
    const bh = ch - pad * 2;
    const size = Math.min(bw, bh / 0.98);
    const ox = pad + (bw - size) / 2;
    const oy = pad + (bh - size * 0.98) / 2;
    const map = (p) => [ox + p[0] * size, oy + p[1] * size * 0.98];
    const g = ctx.createRadialGradient(cw / 2, ch / 2, 10, cw / 2, ch / 2, Math.max(cw, ch) * 0.7);
    g.addColorStop(0, P.skyMid);
    g.addColorStop(1, P.skyTop);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cw, ch);
    for (const poly of [AUSTRALIA, TASMANIA]) {
      ctx.beginPath();
      poly.forEach((p, i) => { const [x, y] = map(p); if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); });
      ctx.closePath();
      ctx.fillStyle = P.mapLand;
      ctx.fill();
      ctx.strokeStyle = P.mapCoast;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
    const reachedIdx = milestone?.reached ? [100, 1000, 10000, 50000, 100000, 250000].indexOf(milestone.reached) + 1 : 0;
    const progress = milestone?.progress ?? 0;
    const glowR = Math.max(10, size * 0.05);
    TOWNS.forEach(([x, y, tier], i) => {
      const [sx, sy] = map([x, y]);
      let on = tier <= reachedIdx;
      // toward the next milestone, the next tier's towns come on one by one
      if (!on && tier === reachedIdx + 1) {
        const group = TOWNS.filter((tw) => tw[2] === tier);
        const rank = group.indexOf(TOWNS[i]) / Math.max(1, group.length);
        on = rank < progress;
      }
      if (on) {
        const tw = opts.reducedMotion ? 1 : 0.85 + 0.15 * Math.sin(t * 0.003 + i);
        glow(ctx, sx, sy, glowR * tw, P.town, 0.55);
        ctx.fillStyle = P.town;
        ctx.beginPath(); ctx.arc(sx, sy, 3, 0, TAU); ctx.fill();
        ctx.fillStyle = P.white;
        ctx.beginPath(); ctx.arc(sx, sy, 1.2, 0, TAU); ctx.fill();
      } else {
        ctx.fillStyle = P.townOff;
        ctx.beginPath(); ctx.arc(sx, sy, 2.2, 0, TAU); ctx.fill();
      }
    });
    // A star over the centre when everything is lit.
    if (reachedIdx >= 6) {
      const [sx, sy] = map([0.5, 0.42]);
      glow(ctx, sx, sy, glowR * 3, P.star, 0.5);
      ctx.fillStyle = P.star;
      starPath(ctx, sx, sy, glowR * 0.8, 5, 0.5);
      ctx.fill();
    }
  }

  // ---------------------------------------------------------------------

  function destroy() {
    glowSprites.clear();
    lvl = null;
  }

  resize();

  return {
    resize, setOptions, setLevel, draw, drawAmbient, drawMyStreet, drawAustralia,
    pushEvents, camera, toWorld, toScreen, destroy,
    get scale() { return scale; },
  };
}
